import { IsString, Matches } from 'class-validator';

/** A day off is a closed date range in the tutor's local calendar. A single day
 *  is a range where `from === to`. Dates are `YYYY-MM-DD` (tutor-local). */
export class DayOffRange {
  @IsString()
  @Matches(/^\d{4}-\d{2}-\d{2}$/, { message: 'from must be YYYY-MM-DD' })
  from!: string;

  @IsString()
  @Matches(/^\d{4}-\d{2}-\d{2}$/, { message: 'to must be YYYY-MM-DD' })
  to!: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * Coerce a raw `dayOffs` value into normalized ranges. Tolerant by design so the
 * backend accepts BOTH the legacy `string[]` (single dates) and the new
 * `DayOffRange[]` shapes during deploy skew and for un-migrated DB rows. Invalid
 * entries are dropped; each range is normalized so `from <= to`.
 */
export function normalizeDayOffs(input: unknown): DayOffRange[] {
  if (!Array.isArray(input)) return [];
  const ranges: DayOffRange[] = [];
  for (const item of input) {
    if (typeof item === 'string') {
      if (DATE_RE.test(item)) ranges.push({ from: item, to: item });
    } else if (isRecord(item)) {
      const from = item.from;
      const to = item.to ?? from;
      if (
        typeof from === 'string' &&
        DATE_RE.test(from) &&
        typeof to === 'string' &&
        DATE_RE.test(to)
      ) {
        ranges.push(from <= to ? { from, to } : { from: to, to: from });
      }
    }
  }
  return ranges;
}

/**
 * Is `localDate` (YYYY-MM-DD, tutor-local) covered by any day-off range?
 * `YYYY-MM-DD` sorts lexicographically the same as chronologically, so plain
 * string comparison is a correct inclusive-range check.
 */
export function isDayOffDate(localDate: string, ranges: DayOffRange[]): boolean {
  return ranges.some((r) => r.from <= localDate && localDate <= r.to);
}
